import React, { useEffect, useState } from 'react';
import { auth, db, googleProvider, appleProvider } from './firebase-config';
import {
  onAuthStateChanged,
  signInWithPopup,
  signOut,
} from 'firebase/auth';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import SignIn from './SignIn';
import UsersList from './UsersList';
import AttemptsTab from './AttemptsTab';
import DTQLocations from './DTQLocations';
import './App.css';
import logo from './logo.jpg';

function App() {
  const [user, setUser] = useState(null);
  const [userDisplayName, setUserDisplayName] = useState('');
  const [userCenter, setUserCenter] = useState('');
  const [isCoordinator, setIsCoordinator] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authError, setAuthError] = useState('');

  useEffect(() => {
    onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        try {
          const usersRef = collection(db, 'users');
          const q = query(usersRef, where('email', '==', currentUser.email));
          const querySnapshot = await getDocs(q);

          if (!querySnapshot.empty) {
            const userDoc = querySnapshot.docs[0].data();
            setUserDisplayName(userDoc.name || currentUser.displayName || currentUser.email || '');
            setUserCenter(userDoc.center || 'Center not set');
            setIsCoordinator(userDoc.coordinator);
            setIsAdmin(userDoc.admin);
          } else {
            console.log('No user data found in Firestore matching the email');
          }
        } catch (error) {
          console.error(error);
          setAuthError(`User profile load failed: ${error.message}`);
        }
      } else {
        setUserDisplayName('');
        setUserCenter('');
        setIsCoordinator(false);
      }
    });
  }, []);

  const signInWithGoogle = async () => {
    setAuthError('');
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      console.error(error);
      setAuthError(`Google sign-in failed: ${error.message}`);
    }
  };

  const signInWithApple = async () => {
    setAuthError('');
    try {
      await signInWithPopup(auth, appleProvider);
    } catch (error) {
      console.error(error);
      setAuthError(`Apple sign-in failed: ${error.message}`);
    }
  };

  const handleLogout = () => {
    signOut(auth).catch(console.error);
  };

  return (
    <div className="App">
      <Router>
        {user && isCoordinator ? (
          <>
            <NavBar
              userDisplayName={userDisplayName}
              userCenter={userCenter}
              isAdmin={isAdmin}
              handleLogout={handleLogout}
            />
            <main className="main-content">
              <Routes>
                <Route path="/" element={<Navigate replace to={isAdmin ? '/locations' : '/users'} />} />
                <Route
                  path="/locations"
                  element={
                    isAdmin ? (
                      <DTQLocations />
                    ) : (
                      <Navigate replace to="/users" />
                    )
                  }
                />
                <Route path="/users" element={<UsersList userCenter={userCenter} isAdmin={isAdmin} />} />
                <Route path="/attempts" element={<AttemptsTab userCenter={userCenter} isAdmin={isAdmin} />} />
                <Route path="*" element={<Navigate replace to={isAdmin ? '/locations' : '/users'} />} />
              </Routes>
            </main>
          </>
        ) : user ? (
          <div className="unauthorized-container">
            <img width="100" height="100" src={logo} alt="Logo" />
            <p>Please contact your administrator.</p>
            <button onClick={handleLogout}>Logout</button>
          </div>
        ) : (
          <div className="signin-container">
            <img width="120" height="120" src={logo} alt="Logo" />
            <h1>DTQ Admin</h1>
            <p>Daura Tarjuma e Quran - Admin Panel</p>
            {authError ? <p className="form-error">{authError}</p> : null}
            <SignIn signInWithGoogle={signInWithGoogle} signInWithApple={signInWithApple} />
          </div>
        )}
      </Router>
    </div>
  );
}

function NavBar({ userDisplayName, userCenter, isAdmin, handleLogout }) {
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="navbar">
      <div className="navbar-brand">
        <img width="40" height="40" src={logo} alt="Logo" className="navbar-logo" />
        <span className="navbar-title">DTQ Admin</span>
      </div>

      <div className="navbar-links">
        {isAdmin && (
          <button
            className={`nav-link ${isActive('/locations') ? 'active' : ''}`}
            onClick={() => navigate('/locations')}
          >
            DTQ Locations
          </button>
        )}
        <button
          className={`nav-link ${isActive('/users') ? 'active' : ''}`}
          onClick={() => navigate('/users')}
        >
          Users
        </button>
        <button
          className={`nav-link ${isActive('/attempts') ? 'active' : ''}`}
          onClick={() => navigate('/attempts')}
        >
          Scores
        </button>
      </div>

      <div className="navbar-right">
        <div className="user-info">
          <span className="user-name">{userDisplayName}</span>
          <span className="user-center">{userCenter}</span>
        </div>
        <button className="btn-logout" onClick={handleLogout}>Logout</button>
      </div>
    </nav>
  );
}

export default App;
